import strawberry from '../../assets/spin-wheel/Strawberry.png';
import orange from '../../assets/spin-wheel/Orange.png';
import banana from '../../assets/spin-wheel/Banana.png';
import mango from '../../assets/spin-wheel/Mango.png';
import melon from '../../assets/spin-wheel/Melon.png';
import pineapple from '../../assets/spin-wheel/Pineapple.png';
import oneGB from '../../assets/spin-wheel/1 GB.png';
import tryAgain from '../../assets/spin-wheel/Try Again.png';
import oneGBCollectedScreen from '../../assets/spin-wheel/1 GB Collected Screen.png';
import allFruitsCollectedScreen from '../../assets/spin-wheel/Alll Fruits Collected Screen.png';
import pineappleCollectedScreen from '../../assets/spin-wheel/PIneapple Collected Screen.png';
import tryAgainScreen from '../../assets/spin-wheel/Try Agan Screen.png';
import bananaBasketGif from '../../assets/spin-wheel/Kito Basket Banana.gif';
import grapeBasketGif from '../../assets/spin-wheel/Kito Basket Grape.gif';
import melonBasketGif from '../../assets/spin-wheel/Kito Basket Watermelon.gif';
import pineappleBasketGif from '../../assets/spin-wheel/Kito Basket Pineapple.gif';
import strawberryBasketGif from '../../assets/spin-wheel/Kito Basket Strawberry.gif';
import fullBasketGif from '../../assets/spin-wheel/Kito Basket full.gif';

export const SPIN_WHEEL_ITEM_TYPES = ['fruit', 'reward', 'try-again'];

// Images that ship inside the app. An item whose `image` matches one of these
// file names is served straight from the bundle - no upload needed.
const BUNDLED_IMAGES = {
  'Strawberry.png': strawberry,
  'Orange.png': orange,
  'Banana.png': banana,
  'Mango.png': mango,
  'Melon.png': melon,
  'Pineapple.png': pineapple,
  '1 GB.png': oneGB,
  'Try Again.png': tryAgain,
  '1 GB Collected Screen.png': oneGBCollectedScreen,
  'Alll Fruits Collected Screen.png': allFruitsCollectedScreen,
  'PIneapple Collected Screen.png': pineappleCollectedScreen,
  'Try Agan Screen.png': tryAgainScreen,
  'Kito Basket Banana.gif': bananaBasketGif,
  'Kito Basket Grape.gif': grapeBasketGif,
  'Kito Basket Watermelon.gif': melonBasketGif,
  'Kito Basket Pineapple.gif': pineappleBasketGif,
  'Kito Basket Strawberry.gif': strawberryBasketGif,
  'Kito Basket full.gif': fullBasketGif,
};

const BUNDLED_IMAGES_BY_LOWERCASE_NAME = Object.entries(BUNDLED_IMAGES).reduce(
  (imagesByName, [fileName, image]) => ({ ...imagesByName, [fileName.toLowerCase()]: image }),
  {}
);

// Pictures that used to ship with the app but were wrong and have been taken
// out. Items in the database may still name them, so they resolve to nothing
// and the page falls back to the item's wheel picture instead of showing the
// wrong fruit or a broken image.
//   - 'Kito Basket Mango.gif' was a copy of the watermelon animation, so a
//     Mango win showed a watermelon dropping into the basket.
const RETIRED_IMAGES = ['kito basket mango.gif'];

// Shown after the last fruit lands in the basket, whatever that fruit was.
export const FULL_BASKET_IMAGE = fullBasketGif;

// Slice colours used when an item has no `color` of its own.
const DEFAULT_SLICE_COLORS = ['#12b0ac', '#e6f8f7'];

// Hosts a full image URL may point at. Pictures are drawn on every child's
// device, so only places we control are allowed; the API enforces the same
// list when an item is saved.
const ALLOWED_IMAGE_HOSTS = ['kitohealth.org', 'cloudfront.net', 'amazonaws.com'];

const HAS_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

const isAllowedImageHost = (hostname) =>
  ALLOWED_IMAGE_HOSTS.some((allowedHost) => hostname === allowedHost || hostname.endsWith(`.${allowedHost}`));

/**
 * A full URL is only used when it is https and on one of our hosts. Anything
 * else with a scheme (data:, blob:, http:, other hosts) is dropped.
 */
export const resolveAbsoluteImageUrl = (imageName) => {
  try {
    const url = new URL(imageName);

    return url.protocol === 'https:' && isAllowedImageHost(url.hostname.toLowerCase()) ? imageName : '';
  } catch {
    return '';
  }
};

/**
 * Turns the `image` field stored in the database into something an <img> can
 * load. Order of resolution:
 *   1. an https URL on one of our hosts, or a root path -> used as it is
 *   2. a file name bundled with the app -> served from the bundle
 *   3. anything else -> looked for in the app's public images folder, so a new
 *      picture can be dropped in without rebuilding the app
 */
export const resolveItemImage = (image) => {
  const imageName = typeof image === 'string' ? image.trim() : '';

  if (!imageName) {
    return '';
  }

  if (HAS_SCHEME_PATTERN.test(imageName)) {
    return resolveAbsoluteImageUrl(imageName);
  }

  // Protocol-relative URLs ('//evil.example/x.png') would leave our origin.
  if (imageName.startsWith('//')) {
    return '';
  }

  if (imageName.startsWith('/')) {
    return imageName;
  }

  if (RETIRED_IMAGES.includes(imageName.toLowerCase())) {
    return '';
  }

  const bundledImage = BUNDLED_IMAGES[imageName] ?? BUNDLED_IMAGES_BY_LOWERCASE_NAME[imageName.toLowerCase()];

  if (bundledImage) {
    return bundledImage;
  }

  return `${process.env.PUBLIC_URL || ''}/images/${encodeURIComponent(imageName)}`;
};

const normalizeType = (type) => (SPIN_WHEEL_ITEM_TYPES.includes(type) ? type : 'fruit');

const normalizePercent = (percent) => {
  const weight = Number(percent);

  return Number.isFinite(weight) && weight > 0 ? weight : 0;
};

// `null` means unlimited stock, which is what fruits and "Try Again" use.
const normalizeQuantity = (quantity) => {
  if (quantity === null || quantity === undefined || quantity === '') {
    return null;
  }

  const remaining = Number(quantity);

  if (!Number.isFinite(remaining)) {
    return null;
  }

  return Math.max(Math.floor(remaining), 0);
};

export const normalizeSpinWheelItem = (item, index = 0) => {
  const name = typeof item?.name === 'string' ? item.name.trim() : '';
  // Mongo hands the record id back as `_id`.
  const { _id: recordId } = item ?? {};

  return {
    id: recordId || item?.id || name || `item-${index}`,
    name,
    type: normalizeType(item?.type),
    image: resolveItemImage(item?.image),
    // An empty result picture falls back to the wheel picture on the page.
    resultImage: (item?.result_image && resolveItemImage(item.result_image)) || null,
    quantity: normalizeQuantity(item?.quantity),
    percent: normalizePercent(item?.percent),
    color: typeof item?.color === 'string' && item.color.trim() ? item.color.trim() : null,
    order: Number.isFinite(Number(item?.order)) ? Number(item.order) : index,
  };
};

const isUsableItem = (item) => Boolean(item.name && item.image);

const hasStockLeft = (item) => item.quantity === null || item.quantity > 0;

const PERCENT_TOTAL = 100;
// Percents are floats, so "adds up to 100" has to allow for rounding dust.
const PERCENT_EPSILON = 1e-9;

export const getTotalPercent = (items) => items.reduce((total, item) => total + item.percent, 0);

/**
 * Spreads the gap between the configured percents and 100 equally over every
 * slice, so the wheel always draws from a full 100%.
 *   -  92% over 8 slices -> every slice gains 1%
 *   - 108% over 8 slices -> every slice loses 1%
 *
 * No slice is ever pushed below 0: when one runs out of percent to give back,
 * the excess that is left over is shared again between the slices that still
 * have something to give.
 */
export const balanceItemPercentages = (items) => {
  if (items.length === 0) {
    return items;
  }

  const difference = PERCENT_TOTAL - getTotalPercent(items);

  if (Math.abs(difference) < PERCENT_EPSILON) {
    return items;
  }

  if (difference > 0) {
    const share = difference / items.length;

    return items.map((item) => ({ ...item, percent: item.percent + share }));
  }

  let percents = items.map((item) => item.percent);
  let excess = -difference;

  while (excess > PERCENT_EPSILON) {
    const absorbingPercents = percents.filter((percent) => percent > PERCENT_EPSILON);

    if (absorbingPercents.length === 0) {
      break;
    }

    const share = excess / absorbingPercents.length;

    excess -= absorbingPercents.reduce((taken, percent) => taken + Math.min(share, percent), 0);
    percents = percents.map((percent) => percent - Math.min(share, percent));
  }

  return items.map((item, index) => ({ ...item, percent: percents[index] }));
};

/**
 * Prepares the raw API payload for the wheel: drops unusable or sold-out items,
 * sorts them by their configured position, gives every slice a colour and
 * balances the percents so they add up to 100.
 */
export const normalizeSpinWheelItems = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return balanceItemPercentages(
    items
      .map((item, index) => normalizeSpinWheelItem(item, index))
      .filter((item) => isUsableItem(item) && hasStockLeft(item))
      .sort((firstItem, secondItem) => firstItem.order - secondItem.order)
      .map((item, index) => ({
        ...item,
        color: item.color ?? DEFAULT_SLICE_COLORS[index % DEFAULT_SLICE_COLORS.length],
      }))
  );
};

export const getFruitNames = (items) => items.filter((item) => item.type === 'fruit').map((item) => item.name);

const ITEM_NAMES_TRANSLATION_KEY = 'staticPages.spinWheel.items';

/**
 * The name to show for an item in the user's language. Item names are stored
 * in English in the database and used as keys for the basket, so the
 * translation lives in the locale files under `staticPages.spinWheel.items`.
 * A name without a translation is shown as it is.
 */
export const getItemDisplayName = (t, name) => {
  if (typeof name !== 'string' || !name) {
    return '';
  }

  const translatedNames = t(ITEM_NAMES_TRANSLATION_KEY, { returnObjects: true });

  if (!translatedNames || typeof translatedNames !== 'object') {
    return name;
  }

  const lowerCaseName = name.toLowerCase();
  const matchingKey = Object.keys(translatedNames).find((key) => key.toLowerCase() === lowerCaseName);
  const translatedName = matchingKey ? translatedNames[matchingKey] : '';

  return typeof translatedName === 'string' && translatedName.trim() ? translatedName : name;
};

// A wheel with no fruits on it has nothing to collect, so its basket counts as
// complete and rewards are not gated behind anything.
export const hasCollectedAllFruits = (items, collectedFruits) =>
  getFruitNames(items).every((fruitName) => collectedFruits.includes(fruitName));

// The draw itself - which slice the wheel stops on - happens on the server.
// The page only animates to the slice it is told about.
